import { Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  filter,
  first,
  forkJoin,
  map,
  mergeMap,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import {
  EmployeeCardView,
  EmployeesPageStats,
} from 'src/app/core/models/company/employee/employee-card-view.interface';
import {
  InvitePermissionGroupView,
  InvitePermissionOptionView,
} from 'src/app/core/models/company/employee/invite-permission-selection.interface';
import { EmployeeListItem } from 'src/app/core/models/company/employee/employee-list-item.interface';
import { InviteFormModel } from 'src/app/core/models/company/employee/invite-form.model';
import { InviteRequestModel } from 'src/app/core/models/company/employee/invite-request.interface';
import { AuthService } from 'src/app/core/services/auth.service';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { InviteService } from 'src/app/core/services/invite.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { InviteHub } from 'src/app/core/signalr-hubs/invite.hub';
import { Constants } from 'src/app/core/constants';
import { FileService } from 'src/app/core/services/file.service';
import { SentInvite } from 'src/app/core/models/sent-invite.interface';
import { InviteStatuses } from 'src/app/core/enums/invite-statuses.enum';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { TariffService } from 'src/app/core/services/tariff.service';
import { TranslateService } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { canInviteByQuota } from './employees-quota.util';

@Component({
    selector: 'app-employees',
    templateUrl: './employees.component.html',
    styleUrls: ['./employees.component.scss'],
    providers: [InviteFormModel],
    standalone: false
})
export class EmployeesComponent implements OnInit {
  inviteFormModel = inject(InviteFormModel);
  private readonly inviteHub = inject(InviteHub);
  private readonly inviteService = inject(InviteService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly employeeService = inject(EmployeeService);
  private readonly fileService = inject(FileService);
  private readonly permissionService = inject(PermissionService);
  private readonly tariffService = inject(TariffService);
  private readonly translateService = inject(TranslateService);
  private route = inject(ActivatedRoute);

  private static readonly inviteResourceTypeMap = {
    COMPANY: ResourceTypes.Company,
    PROJECT: ResourceTypes.Project,
    ASSIGNMENT: ResourceTypes.Assignment,
    EMPLOYEE: ResourceTypes.Employee,
    CONTACT: ResourceTypes.Contact,
    ORGANIZATION: ResourceTypes.Organization,
  } as const;

  @ViewChild('inviteSuccessTpl')
  public inviteSuccessTpl!: TemplateRef<unknown>;
  @ViewChild('inviteErrorTpl')
  public inviteErrorTpl!: TemplateRef<unknown>;

  public readonly notFound = Constants.NotFound;
  public readonly userRoles = Constants.UserRolesI18n;
  public readonly inviteStatuses = InviteStatuses;
  public readonly isMobile = Constants.IsMobile;
  public readonly canCollapseInvites =
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 1199px)').matches : false;
  public readonly invitePermissionGroups: InvitePermissionGroupView[] =
    this.buildInvitePermissionGroups();

  public readonly currentLang: string;
  public readonly employeeLimitParam: Record<string, string> = {
    uk: 'працівників',
    en: 'employees',
  };
  public readonly inviteEmailControl = this.inviteFormModel.form.controls[
    'recipientEmail'
  ] as FormControl<string>;

  public readonly canManageEmployees$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Employee,
          UserCompanyPermissions.Edit,
        ),
      ),
      shareReplay(),
    );

  public callbackError?: string;
  public isCollapsed = false;
  public isInviteModalOpen = false;
  public isEmployeeProfileModalOpen = false;
  public selectedEmployeeId: string | null = null;
  public showEmployeeProfileDeleteButton = false;
  public showEmployeeProfilePermissions = false;

  public sentInvites$ = new BehaviorSubject<SentInvite[]>([]);
  public employees$ = new BehaviorSubject<EmployeeListItem[]>([]);
  public readonly normalizedSentInvites$ = this.sentInvites$.pipe(
    map((invites) => invites
      .map((invite) => this.normalizeSentInvite(invite))
      .filter((invite): invite is SentInvite => !!invite)),
    shareReplay(1),
  );
  public employeeCards$ = this.employees$.pipe(
    map((employees) =>
      employees.map((employee) => this.toEmployeeCardView(employee)),
    ),
  );
  public inviteCards$ = this.normalizedSentInvites$.pipe(
    map((invites) =>
      [...invites].sort(
        (left, right) =>
          this.getInviteStatusSortOrder(left.status) -
          this.getInviteStatusSortOrder(right.status),
      ),
    ),
    shareReplay(1),
  );
  public readonly inviteSidebarView$ = combineLatest([
    this.canManageEmployees$,
    this.inviteCards$,
  ]).pipe(
    map(([canManageEmployees, inviteCards]) => ({
      canManageEmployees,
      inviteCards,
      shouldShowSidebar: canManageEmployees || inviteCards.length > 0,
    })),
    shareReplay(1),
  );
  public pageStats$ = combineLatest([
    this.employeeCards$,
    this.normalizedSentInvites$,
  ]).pipe(
    map(
      ([employees, sentInvites]): EmployeesPageStats => ({
        membersCount: employees.length,
        onlineCount: employees.filter((employee) => employee.isOnline).length,
        invitedCount: sentInvites.filter(
          (invite) => invite.status === InviteStatuses.Sent,
        ).length,
        rejectedCount: sentInvites.filter(
          (invite) => invite.status === InviteStatuses.Rejected,
        ).length,
      }),
    ),
  );

  public canAddEmployee$ = combineLatest([
    this.tariffService.companyQuotas$,
    this.employees$,
    this.normalizedSentInvites$,
  ]).pipe(
    map(([companyQuotas, employees, sentInvites]) => {
      const currentUsage = sentInvites.length + employees.length;

      return canInviteByQuota(
        companyQuotas,
        Constants.QuotaKeys.maxUsers,
        currentUsage,
      );
    }),
  );

  public companyId!: string;
  private hubListenersRegistered = false;

  private employeesWithStatuses$ = this.employees$.pipe(
    first(),
    switchMap((employees) =>
      this.getEmployeesWithStatuses(employees).pipe(
        tap((employeesWithStatuses) => {
          this.employees$.next(employeesWithStatuses);
        }),
      ),
    ),
  );

  constructor() {
    this.currentLang =
      this.translateService.currentLang ?? Constants.DefaultLanguage;
    this.inviteFormModel.initPermissionControls(this.invitePermissionGroups);
  }

  ngOnInit(): void {
    const initUrls$ = this.route.parent?.parent?.params.pipe(
      first(),
      tap((params) => {
        this.companyId = params['id'];
        this.employeeService.setEmployeesUrl(this.companyId);
        this.tariffService.setCompanyId(this.companyId);
      }),
      shareReplay(1),
    );

    initUrls$
      ?.pipe(
        switchMap(() =>
          this.inviteHub.connectionSucceed$.pipe(filter((x) => !!x)),
        ),
        switchMap(() => this.loadEmployees()),
      )
      .subscribe();

    initUrls$!
      .pipe(
        switchMap(() => this.inviteService.getSentInvites(this.companyId)),
        tap((invites) => this.sentInvites$.next(invites)),
      )
      .subscribe();

    this.listenUsers();
  }

  public invite(): void {
    if (this.inviteFormModel.form.invalid) {
      this.inviteEmailControl.markAsTouched();
      return;
    }

    const request: InviteRequestModel = {
      companyId: this.companyId,
      senderId: this.authService.user$.getValue().data!.id,
      recipientEmail: this.inviteEmailControl.value.trim(),
      permissions:
        this.inviteFormModel.getSelectedPermissions(this.invitePermissionGroups),
    };

    this.inviteService
      .inviteUserToCompany(request)
      .pipe(
        first(),
        tap((sentInvite) => {
          this.inviteFormModel.form.reset();
          this.resetInvitePermissions();
          this.isInviteModalOpen = false;

          this.toastService.show({
            template: this.inviteSuccessTpl,
            className: 'bg-success text-light',
          });

          this.sentInvites$.next([...this.sentInvites$.getValue(), sentInvite]);

          this.inviteHub.sendInviteSentNotification(request.recipientEmail);
        }),
        catchError((error) => {
          this.callbackError = error.message;

          this.toastService.show({
            template: this.inviteErrorTpl,
            className: 'bg-danger text-light',
          });

          return EMPTY;
        }),
      )
      .subscribe();
  }

  public viewProfile(employeeId: string, employeeRole: string): void {
    this.selectedEmployeeId = employeeId;
    this.isEmployeeProfileModalOpen = true;
    this.showEmployeeProfileDeleteButton = false;
    this.showEmployeeProfilePermissions = false;

    this.canManageEmployees$
      .pipe(
        first(),
        tap((res) => {
          const showConfigs =
            res &&
            this.authService.user$.getValue().data!.id !== employeeId &&
            employeeRole !== Constants.UserRoles.companyAdmin;

          this.showEmployeeProfileDeleteButton = showConfigs;
          this.showEmployeeProfilePermissions = showConfigs;
        }),
      )
      .subscribe();
  }

  public resendInvite(inviteId: string, recipientEmail: string): void {
    this.inviteService
      .resendInvite(inviteId)
      .pipe(
        first(),
        tap(() => {
          this.toastService.show({
            template: this.inviteSuccessTpl,
            className: 'bg-success text-light',
          });

          this.sentInvites$.next(
            this.sentInvites$
              .getValue()
              .map((i) =>
                i.id === inviteId ? { ...i, status: InviteStatuses.Sent } : i,
              ),
          );

          this.inviteHub.sendInviteSentNotification(recipientEmail);
        }),
      )
      .subscribe();
  }

  public deleteInvite(inviteId: string): void {
    this.inviteService
      .deleteInvite(inviteId)
      .pipe(
        first(),
        tap(() =>
          this.sentInvites$.next(
            this.sentInvites$.getValue().filter((i) => i.id !== inviteId),
          ),
        ),
      )
      .subscribe();
  }

  public toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  public openInviteModal(): void {
    this.isInviteModalOpen = true;
  }

  public closeInviteModal(): void {
    this.isInviteModalOpen = false;
    this.inviteFormModel.form.reset({ recipientEmail: '' });
    this.inviteFormModel.form.markAsPristine();
    this.inviteFormModel.form.markAsUntouched();
    this.resetInvitePermissions();
  }

  public closeEmployeeProfileModal(): void {
    this.isEmployeeProfileModalOpen = false;
    this.selectedEmployeeId = null;
    this.showEmployeeProfileDeleteButton = false;
    this.showEmployeeProfilePermissions = false;
  }

  public handleEmployeeDeleted(employeeId: string): void {
    this.employees$
      .pipe(
        first(),
        tap((employees) => {
          const updatedEmployees = employees.filter((employee) => employee.id !== employeeId);
          this.employees$.next(updatedEmployees);
          this.closeEmployeeProfileModal();
        }),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public handleSelectedEmployeeDeleted(): void {
    if (!this.selectedEmployeeId) {
      return;
    }

    this.handleEmployeeDeleted(this.selectedEmployeeId);
  }

  private getEmployeesWithStatuses(
    employees: EmployeeListItem[],
  ): Observable<EmployeeListItem[]> {
    return this.employeeService.getConnectedEmployeeIds().pipe(
      map((ids) =>
        employees.map((e) => {
          e.status = ids.includes(e.id);
          return e;
        }),
      ),
    );
  }

  private listenUsers(): void {
    this.inviteHub.restartConnection(
      this.authService.user$.getValue().accessToken,
    );
    this.registerHubListeners();
  }

  private registerHubListeners(): void {
    if (this.hubListenersRegistered || !this.inviteHub.hubConnection) {
      return;
    }

    this.hubListenersRegistered = true;

    this.inviteHub.hubConnection.on('inviteRejected', (inviteId) => {
      this.sentInvites$.next(
        this.sentInvites$
          .getValue()
          .map((invite) =>
            invite.id === inviteId
              ? { ...invite, status: InviteStatuses.Rejected }
              : invite,
          ),
      );
    });

    this.inviteHub.hubConnection.on('inviteAccepted', (inviteId) => {
      this.sentInvites$.next(
        this.sentInvites$
          .getValue()
          .map((invite) =>
            invite.id === inviteId
              ? { ...invite, status: InviteStatuses.Accepted }
              : invite,
          ),
      );
      this.loadEmployees().subscribe();
    });

    this.inviteHub.hubConnection.on('userConnected', () => {
      this.employeesWithStatuses$.subscribe();
    });

    this.inviteHub.hubConnection.on('userDisconnected', () => {
      this.employeesWithStatuses$.subscribe();
    });
  }

  private toEmployeeCardView(employee: EmployeeListItem): EmployeeCardView {
    const displayName = employee.fullName ?? employee.userName;
    const initials = displayName
      .split(' ')
      .filter((part) => !!part)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');

    return {
      id: employee.id,
      displayName,
      role: employee.role,
      roleLabelKey: this.userRoles.get(employee.role)!,
      email: employee.email || '',
      hasEmail: !!employee.email,
      phoneNumber: employee.phoneNumber || '',
      joinedAt: this.formatJoinDate(employee.joinedAt),
      imageUrl: employee.imageUrl,
      hasImage: employee.imageUrl !== this.notFound,
      initials,
      isOnline: employee.status,
    };
  }

  private formatJoinDate(joinedAt?: string): string {
    if (!joinedAt) {
      return '';
    }

    const parsedDate = new Date(joinedAt);

    if (Number.isNaN(parsedDate.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(this.currentLang, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(parsedDate);
  }

  private getInviteStatusSortOrder(status: InviteStatuses): number {
    switch (status) {
      case InviteStatuses.Sent:
        return 0;
      case InviteStatuses.Rejected:
        return 1;
      case InviteStatuses.Accepted:
        return 2;
      default:
        return 3;
    }
  }

  private normalizeSentInvite(invite: SentInvite | null | undefined): SentInvite | null {
    if (!invite?.id || !invite.recipientEmail?.trim()) {
      return null;
    }

    if (!Object.values(InviteStatuses).includes(invite.status)) {
      return null;
    }

    return {
      ...invite,
      recipientEmail: invite.recipientEmail.trim(),
    };
  }

  private loadEmployees(): Observable<EmployeeListItem[]> {
    return this.employeeService.getEmployees().pipe(
      mergeMap((employees) => {
        const observableEmployees = employees.map((employee) => {
          if (!employee.avatarImageExtension) {
            employee.imageUrl = Constants.NotFound;
            return of(employee);
          }

          return this.fileService.getEntityWithImageUrl(
            employee,
            `users/${employee.id}/avatar${employee.avatarImageExtension}`,
          );
        });

        return forkJoin(observableEmployees);
      }),
      switchMap((employees) => this.getEmployeesWithStatuses(employees)),
      tap((employeesWithStatuses) => {
        this.employees$.next(employeesWithStatuses);
      }),
    );
  }

  private buildInvitePermissionGroups(): InvitePermissionGroupView[] {
    return [
      this.createInvitePermissionGroup('COMPANY', ['Edit', 'Delete']),
      this.createInvitePermissionGroup('PROJECT', ['View', 'Create', 'Edit', 'Delete']),
      this.createInvitePermissionGroup('ASSIGNMENT', ['View', 'Create', 'Edit', 'Delete']),
      this.createInvitePermissionGroup('EMPLOYEE', ['View', 'Edit']),
      this.createInvitePermissionGroup('CONTACT', ['View', 'Create', 'Edit', 'Delete']),
      this.createInvitePermissionGroup('ORGANIZATION', ['View', 'Create', 'Edit', 'Delete']),
    ];
  }

  private createInvitePermissionGroup(
    resourceTypeKey: keyof typeof EmployeesComponent.inviteResourceTypeMap,
    permissionKeys: Array<keyof typeof UserCompanyPermissions>,
  ): InvitePermissionGroupView {
    const resourceType = EmployeesComponent.inviteResourceTypeMap[resourceTypeKey];

    return {
      titleKey: `RESOURCE_TYPES.${resourceTypeKey}`,
      options: permissionKeys.map((permissionKey) =>
        this.createInvitePermissionOption(resourceTypeKey, permissionKey, resourceType),
      ),
    };
  }

  private createInvitePermissionOption(
    resourceTypeKey: keyof typeof EmployeesComponent.inviteResourceTypeMap,
    permissionKey: keyof typeof UserCompanyPermissions,
    resourceType: ResourceTypes,
  ): InvitePermissionOptionView {
    const permissionCode = UserCompanyPermissions[permissionKey];
    const controlName = `${resourceTypeKey.toLowerCase()}${permissionKey}`;
    const selected = this.isDefaultInvitePermission(resourceType, permissionCode);

    return {
      id: `invite-permission-${controlName}`,
      labelKey: `PERMISSIONS.${permissionKey.toUpperCase()}`,
      controlName,
      control: new FormControl(selected, { nonNullable: true }),
      resourceType,
      permissionCode,
      selected,
    };
  }

  private isDefaultInvitePermission(
    resourceType: ResourceTypes,
    permissionCode: UserCompanyPermissions,
  ): boolean {
    return (
      permissionCode === UserCompanyPermissions.View &&
      [
        ResourceTypes.Project,
        ResourceTypes.Assignment,
        ResourceTypes.Contact,
        ResourceTypes.Organization,
      ].includes(resourceType)
    );
  }

  private resetInvitePermissions(): void {
    this.invitePermissionGroups.forEach((group) => {
      group.options.forEach((option) => {
        this.inviteFormModel.form.controls[option.controlName]?.setValue(option.selected);
      });
    });
  }
}
